import { movies } from "@/mocks/movies";
import { genreOptions, regionOptions, sortOptions } from "@/mocks/site";
import type { MovieItem } from "@/types/movie";

export const allMovies = movies as MovieItem[];

export function getMovieById(id: string | undefined): MovieItem | undefined {
  if (!id) return undefined;
  return allMovies.find((m) => m.id === id);
}

export function getMediaTypeLabel(type: string | undefined): string {
  if (type === "movie") return "欧美电影";
  if (type === "tv") return "欧美电视剧";
  return "全部资源";
}

export function genreSlugToLabel(slug: string | undefined): string | undefined {
  if (!slug) return undefined;
  return genreOptions.find((g) => g.value === slug)?.label;
}

export function genreLabelToSlug(label: string): string | undefined {
  return genreOptions.find((g) => g.label === label)?.value;
}

export function regionSlugToLabel(slug: string | undefined): string | undefined {
  if (!slug) return undefined;
  return regionOptions.find((r) => r.value === slug)?.label;
}

export function regionLabelToSlug(label: string): string | undefined {
  return regionOptions.find((r) => r.label === label)?.value;
}

export function sortLabel(value: string | undefined): string {
  return sortOptions.find((s) => s.value === value)?.label ?? sortOptions[0].label;
}

export interface FilterState {
  type?: string;
  genres: string[];
  regions: string[];
  years: string[];
  keyword?: string;
  sort?: string;
}

export function filterMovies(source: MovieItem[], state: FilterState): MovieItem[] {
  const genreLabels = state.genres
    .map((g) => genreSlugToLabel(g))
    .filter((v): v is string => Boolean(v));
  const regionLabels = state.regions
    .map((r) => regionSlugToLabel(r))
    .filter((v): v is string => Boolean(v));
  const keyword = (state.keyword ?? "").trim().toLowerCase();

  const result = source.filter((item) => {
    if (state.type && state.type !== "all" && item.type !== state.type) return false;
    if (genreLabels.length && !item.genres.some((g) => genreLabels.includes(g))) return false;
    if (regionLabels.length && !regionLabels.includes(item.region)) return false;
    if (state.years.length && !state.years.includes(String(item.year))) return false;
    if (keyword) {
      const haystack = `${item.title} ${item.summary} ${item.director} ${item.genres.join(" ")}`.toLowerCase();
      if (!haystack.includes(keyword)) return false;
    }
    return true;
  });

  return sortMovies(result, state.sort);
}

export function sortMovies(list: MovieItem[], sort: string | undefined): MovieItem[] {
  const copy = [...list];
  switch (sort) {
    case "hot":
      return copy.sort((a, b) => b.hot - a.hot);
    case "rating":
      return copy.sort((a, b) => b.rating - a.rating);
    case "latest":
    default:
      return copy.sort(
        (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
      );
  }
}

export function searchMovies(keyword: string): MovieItem[] {
  const k = keyword.trim().toLowerCase();
  if (!k) return [];
  return allMovies.filter((item) => {
    const haystack = `${item.title} ${item.summary} ${item.director} ${item.genres.join(" ")} ${item.region}`.toLowerCase();
    return haystack.includes(k);
  });
}

export function getRelated(movie: MovieItem, count = 4): MovieItem[] {
  const scored = allMovies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      if (m.type === movie.type) score += 2;
      if (m.region === movie.region) score += 2;
      score += m.genres.filter((g) => movie.genres.includes(g)).length * 3;
      return { item: m, score };
    })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || b.item.hot - a.item.hot);
  return scored.slice(0, count).map((s) => s.item);
}

export function getLatest(count = 8): MovieItem[] {
  return sortMovies(allMovies, "latest").slice(0, count);
}

export function getHot(count = 8): MovieItem[] {
  return sortMovies(allMovies, "hot").slice(0, count);
}

export function getFeatured(): MovieItem[] {
  return allMovies.filter((m) => m.featured);
}